import * as React from 'react';
import Pagination from '@mui/material/Pagination';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Button, TextField, Typography } from '@mui/material';
import { alertError } from '../alert/swal';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { colors } from '../../constants/colors';
import { useTranslation } from 'react-i18next';

type Props = {
    page: number;
    pageLimit: number;
    totalItems: number;
    setPage: (new_page: number) => void;
    next_button_text?: string;
    prev_button_text?: string;
};

const PaginationCustom: React.FC<Props> = ({
    page,
    pageLimit,
    setPage,
    totalItems,
    next_button_text,
    prev_button_text,
}) => {
    const { t } = useTranslation();
    const totalPages = Math.ceil(totalItems / pageLimit);
    const [inputPage, setInputPage] = React.useState<string>(page.toString());
    const [error, setError] = React.useState<string>('');

    React.useEffect(() => {
        setInputPage(page.toString()); // Sync input with page when page changes externally
    }, [page]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) {
            setInputPage(val);
            setError('');
        }
    };

    const handleChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setPage(value);
    };

    const handleSearch = () => {
        const pageNum = Number(inputPage);
        if (!pageNum || pageNum < 1 || pageNum > totalPages) {
            alertError(t("กรุณาป้อนเลขหน้าตั้งแต่ 1 ถึง {{totalPages}}", { totalPages }))
            return;
        }
        setPage(pageNum);
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    return (
        <Box
            display="flex"
            my={4}
            alignItems="center"
            flexWrap="nowrap"
            overflow="auto"
            whiteSpace="nowrap"
            gap={2}
            sx={{
                justifyContent: { xs: "start", xl: "flex-end" },
                '& > *': { flexShrink: 0 } // ป้องกันปุ่มย่อเกินไป
            }}
        >
            {/* Pagination */}
            <Pagination
                page={page}
                onChange={handleChange}
                count={totalPages}
                renderItem={(item) => {
                    if (item.type === 'previous') {
                        return (
                            <Button
                                key="previous"
                                sx={{
                                    borderRadius: '8px',
                                    border: '2px solid #DDE1EB',
                                    color: "#000",
                                    flexShrink: 0,
                                }}
                                onClick={item.onClick}
                                disabled={item.disabled}
                                startIcon={<ArrowBackIcon sx={{
                                    color: "#000"
                                }} />}
                                variant="outlined"
                            >
                                <Typography sx={{
                                    color: "#000"
                                }}>
                                    {prev_button_text || 'Back'}
                                </Typography>
                            </Button>
                        );
                    } else if (item.type === 'next') {
                        return (
                            <Button
                                key="next"
                                sx={{
                                    borderRadius: '8px',
                                    border: '2px solid #DDE1EB',
                                    color: "#000",
                                    mx: 0.5,
                                    flexShrink: 0,
                                }}
                                onClick={item.onClick}
                                disabled={item.disabled}
                                endIcon={<ArrowForwardIcon sx={{
                                    color: "#000",
                                }} />}
                                variant="outlined"
                            >
                                <Typography sx={{
                                    color: "#000",
                                }}>
                                    {next_button_text || 'Next'}
                                </Typography>
                            </Button>
                        );
                    }
                    return (
                        <Button
                            key={item.page}
                            color="info"
                            disabled={item.disabled}
                            sx={(theme) => ({
                                minWidth: item.page && item.page > 10000 ? 80 : 38,
                                maxWidth: 32,
                                borderRadius: '8px',
                                border: '2px solid #DDE1EB',
                                mx: 0.5,
                                bgcolor: item.selected ? colors.themeMainColor : undefined,
                                color: item.selected ? 'white' : "#000",
                                flexShrink: 0,
                            })}
                            onClick={item.page ? item.onClick : undefined}
                            variant="outlined"
                        >
                            {item.page || <MoreVertIcon sx={{ rotate: "90deg" }} />}
                        </Button>
                    );
                }}
            />

            {/* Input + Go */}
            <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
                <Typography sx={{ color: "#000" }}>Page</Typography>
                <TextField
                    value={inputPage}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    error={!!error}
                    size="small"
                    sx={{ maxWidth: inputPage.length == 3 ? 70 : inputPage.length >= 4 ? 120 : 51 }}

                    inputProps={{
                        sx: {
                            color: "#000",
                        }, inputMode: 'numeric', pattern: '[0-9]*'
                    }}
                />
                <Button sx={{ border: 'none', color: "#000", }} variant="outlined" onClick={handleSearch}>
                    Go
                </Button>
            </Box>
        </Box>
    );
};

export default PaginationCustom;
